import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Dispatch, SetStateAction } from 'react';

import StarIcon from '@/fo/icons/base/icons/star.svg';
import { UiProductOptionsBase } from '@/fo/ui/product/options/base';
import { utilEventConfigurableProduct } from '@/fo/util/event-configurable-product';
import { MappedProduct, Option } from '@/fo/util/map-product-data';

import { styles } from './ProductInfo.styles';

interface ProductInfoProps
  extends Pick<
    MappedProduct,
    'thumbnail' | 'name' | 'rating' | 'configurableVariants'
  > {
  setConfigurableVariants: Dispatch<
    SetStateAction<MappedProduct['configurableVariants']>
  >;
}

export function ProductInfo({
  thumbnail,
  name,
  rating,
  configurableVariants,
  setConfigurableVariants
}: ProductInfoProps) {
  const slots = styles();
  const t = useTranslations();

  const onOptionClick = (option: Option) => {
    const { sku, product } = option;

    if (!product) return;

    utilEventConfigurableProduct.selectProduct(product);
    setConfigurableVariants((prev) =>
      prev?.map((item) => ({
        ...item,
        isSelected: item.sku === sku
      }))
    );
  };

  return (
    <div className={slots.base()}>
      {thumbnail && (
        <Image
          className={slots.image()}
          src={thumbnail.src}
          width={60}
          height={60}
          alt={thumbnail.alt}
        />
      )}
      <div className={slots.content()}>
        <h1 className={slots.name()}>
          <span className={slots.nameText()}>{name}</span>
        </h1>

        <div className={slots.additionalInfo()}>
          {configurableVariants && (
            <UiProductOptionsBase
              size="l"
              options={configurableVariants}
              onOptionClick={(option) =>
                option.isSelected ? null : onOptionClick(option)
              }
            />
          )}
          <Link
            href="#review"
            className={slots.link()}
          >
            <StarIcon
              className={slots.starIcon({
                rating: !!rating
              })}
            />
            {rating ? (
              <>
                {rating} / {t('Reviews')}
              </>
            ) : (
              t('review.empty.reviews')
            )}
          </Link>
        </div>
      </div>
    </div>
  );
}
